import { ReactNode } from 'react';

/**
 * UiPageHeader - Consistent page-level header matching .sub-header.centered
 *
 * Used for main page titles like "VENDOR CODE MAINTENANCE - UPDATE VENDOR CODE"
 */

export interface UiPageHeaderProps {
  children: ReactNode;
  centered?: boolean;
  /** Force uppercase text (default: true for 'sub' variant; false for 'primary'). */
  uppercase?: boolean;
  /**
   * Visual variant.
   * - 'sub' (default): h4 matching .sub-header (18px bold, uppercase) — unchanged from original.
   * - 'primary': h1 matching AngularJS `<h1 class="primary-headline">` which inherits
   *   browser-default h1 styling (font-size 2em / ~32px, bold, margin 0.67em 0).
   */
  variant?: 'sub' | 'primary';
  className?: string;
}

export const UiPageHeader = ({
  children,
  centered = false,
  uppercase,
  variant = 'sub',
  className = '',
}: UiPageHeaderProps) => {
  const alignmentClasses = centered ? 'text-center' : '';

  if (variant === 'primary') {
    // AngularJS `<h1 class="primary-headline">` — the CSS class has no rules defined
    // anywhere, so it renders with browser-default h1 styling:
    //   font-size: 2em (~32px); font-weight: bold; margin: 0.67em 0; display: block;
    const useUppercase = uppercase === true; // primary-headline is NOT uppercase by default
    const primaryClasses = `text-[2em] font-bold my-[0.67em] block text-[#333]${useUppercase ? ' uppercase' : ''}`;
    return (
      <h1 className={`${primaryClasses} ${alignmentClasses} ${className}`.trim()}>{children}</h1>
    );
  }

  // Matches h4.sub-header styling (default / existing behaviour):
  // - text-[18px] (Bootstrap h4 default: font-size: 18px)
  // - font-bold
  // - uppercase (optional via prop)
  // - text-[#333]
  // - leading-[1.1] (Bootstrap h4 line-height)
  // - mb-[10px] for spacing
  // - border-bottom: none (.block-section .sub-header)
  const useUppercase = uppercase !== false; // default true for 'sub'
  const baseClasses = `text-[18px] font-bold text-[#333] mb-[10px] mt-[10px] leading-[1.1]${useUppercase ? ' uppercase' : ''}`;

  return <h4 className={`${baseClasses} ${alignmentClasses} ${className}`.trim()}>{children}</h4>;
};
